"use client";

import { ChevronDown, Search, X } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from "react";
import { createPortal } from "react-dom";

import { cn } from "@/lib/utils/cn";
import { useOverlayPosition } from "./use-overlay-position";

export type ThemedMultiSelectOption = {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
};

export type ThemedMultiSelectProps = {
  value: string[];
  onChange: (next: string[]) => void;
  options: ThemedMultiSelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  loading?: boolean;
  className?: string;
  triggerClassName?: string;
  menuClassName?: string;
  optionClassName?: string;
  ariaLabel?: string;
  maxSelectedLabels?: number;
};

function toggleValue(selected: string[], nextValue: string): string[] {
  return selected.includes(nextValue)
    ? selected.filter((value) => value !== nextValue)
    : [...selected, nextValue];
}

export function ThemedMultiSelect({
  value,
  onChange,
  options,
  placeholder = "Select",
  searchPlaceholder = "Search...",
  disabled = false,
  invalid = false,
  loading = false,
  className,
  triggerClassName,
  menuClassName,
  optionClassName,
  ariaLabel,
  maxSelectedLabels = 2,
}: ThemedMultiSelectProps) {
  const autoId = useId();
  const listId = `${autoId}-multi-list`;
  const searchId = `${autoId}-multi-search`;
  const rootRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const placement = useOverlayPosition(open, rootRef, "bottom-start", "anchor", 4);

  useEffect(() => {
    if (!open) {
      setSearch("");
      return;
    }
    const frame = window.requestAnimationFrame(() => {
      searchRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [open]);

  useEffect(() => {
    function onDoc(event: MouseEvent) {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if ((disabled || loading) && open) {
      setOpen(false);
    }
  }, [disabled, loading, open]);

  const selectedOptions = useMemo(
    () => options.filter((option) => value.includes(option.value)),
    [options, value],
  );

  const filteredOptions = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) {
      return options;
    }
    return options.filter((option) =>
      `${option.label} ${option.description ?? ""}`.toLowerCase().includes(needle),
    );
  }, [options, search]);

  function removeChip(event: ReactMouseEvent, optionValue: string) {
    event.preventDefault();
    event.stopPropagation();
    onChange(value.filter((item) => item !== optionValue));
  }

  const summary =
    selectedOptions.length === 0
      ? placeholder
      : selectedOptions.length <= maxSelectedLabels
        ? selectedOptions.map((option) => option.label).join(", ")
        : `${selectedOptions.length} selected`;

  const menu =
    open && placement ? (
      <div
        ref={menuRef}
        style={placement}
        className={cn(
          "overflow-hidden rounded-card border border-card-border bg-surface-elevated p-ds-1 shadow-overlay",
          menuClassName,
        )}
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="border-b border-border/50 px-2 pb-2 pt-1">
          <label htmlFor={searchId} className="sr-only">
            Search
          </label>
          <div className="flex items-center gap-2 rounded-control border border-card-border bg-surface-inset px-3 py-2">
            <Search className="h-4 w-4 text-muted" aria-hidden />
            <input
              id={searchId}
              ref={searchRef}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={searchPlaceholder}
              className="w-full border-0 bg-transparent text-caption text-foreground outline-none placeholder:text-muted"
            />
          </div>
        </div>
        <div
          role="listbox"
          aria-multiselectable
          aria-label={ariaLabel}
          id={listId}
          className="max-h-[min(18rem,calc(100vh-12rem))] overflow-y-auto pr-1"
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => {
              const active = value.includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={active}
                  disabled={option.disabled}
                  onClick={() => {
                    if (option.disabled) {
                      return;
                    }
                    onChange(toggleValue(value, option.value));
                  }}
                  className={cn(
                    "mt-1.5 flex w-full min-w-0 items-start gap-2 rounded-control border px-3 py-2.5 text-left transition-colors duration-fast disabled:cursor-not-allowed disabled:opacity-50",
                    active
                      ? "border-accent/45 bg-accent-muted text-foreground shadow-[inset_0_0_0_1px_var(--accent-primary-soft)]"
                      : "border-transparent text-muted hover:border-accent/20 hover:bg-surface-hover hover:text-foreground",
                    optionClassName,
                  )}
                >
                  <input
                    type="checkbox"
                    checked={active}
                    readOnly
                    tabIndex={-1}
                    aria-hidden
                    className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--accent)]"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-caption font-semibold">{option.label}</span>
                    {option.description ? (
                      <span className={cn("mt-1 block truncate text-micro", active ? "text-accent" : "text-muted")}>
                        {option.description}
                      </span>
                    ) : null}
                  </span>
                </button>
              );
            })
          ) : (
            <div className="p-3 text-center text-micro text-muted">No matches found.</div>
          )}
        </div>
      </div>
    ) : null;

  return (
    <>
      <div ref={rootRef} className={cn("relative", className)}>
        <button
          type="button"
          disabled={disabled || loading}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listId}
          aria-label={ariaLabel}
          aria-invalid={invalid || undefined}
          onClick={() => setOpen((current) => !current)}
          className={cn(
            "ui-control inline-flex min-h-10 w-full cursor-pointer items-center gap-2 rounded-control border-card-border bg-surface-primary px-3 py-2 text-left text-caption font-medium text-foreground hover:border-accent/30 focus-visible:border-accent focus-visible:ring-accent/20 disabled:cursor-not-allowed disabled:opacity-55 aria-[invalid=true]:border-status-error aria-[invalid=true]:shadow-[0_0_0_3px_var(--semantic-error-surface)]",
            triggerClassName,
          )}
        >
          <span className="min-w-0 flex-1">
            {loading ? <span className="truncate text-muted">Loading…</span> : selectedOptions.length > 0 ? (
              <span className="flex flex-wrap items-center gap-1.5">
                {selectedOptions.slice(0, maxSelectedLabels).map((option) => (
                  <span
                    key={option.value}
                    className="inline-flex max-w-full items-center gap-1 rounded-xs border border-accent/25 bg-accent-muted pl-1.5 pr-0.5 text-micro font-medium text-foreground shadow-raised"
                  >
                    <span className="max-w-[8rem] truncate">{option.label}</span>
                    <button
                      type="button"
                      className="rounded-xs p-0.5 text-muted hover:bg-accent-muted hover:text-accent"
                      aria-label={`Remove ${option.label}`}
                      onClick={(event) => removeChip(event, option.value)}
                    >
                      <X className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  </span>
                ))}
                {selectedOptions.length > maxSelectedLabels ? (
                  <span className="text-micro text-muted">+{selectedOptions.length - maxSelectedLabels} more</span>
                ) : null}
              </span>
            ) : (
              <span className="truncate text-muted">{summary}</span>
            )}
          </span>
          <ChevronDown
            className={cn("h-4 w-4 shrink-0 text-muted transition-transform", open && "rotate-180")}
            aria-hidden
          />
        </button>
      </div>
      {typeof document !== "undefined" && menu ? createPortal(menu, document.body) : null}
    </>
  );
}
